import type { ReactElement } from "react";
import { ErrorPage } from "@pages/ErrorPage";
import { useIsStoryblokEditor } from "@hooks/useIsStoryblokEditor";

/**
 * Shared guard for the content pages. Inside the Visual Editor, when a draft fails to load
 * (`failed` — e.g. an invalid/expired token or a story that isn't saved yet), returns the
 * ErrorPage with editor-aware copy to render in place, instead of the normal `<Navigate>` to
 * /404. Navigating would strip the signed `_storyblok_tk` params from the URL and break the
 * live editor session, so editors stay put and get a clear message. Returns `null` when not in
 * the editor (or when nothing failed) so the page renders normally.
 *
 * Call it at the top level (it is a hook) and early-return when non-null:
 *   const fallback = useEditorPreviewFallback(!isLoading && !!error);
 *   if (fallback) return fallback;
 */
export const useEditorPreviewFallback = (
  failed: boolean
): ReactElement | null => {
  const isEditor = useIsStoryblokEditor();
  return isEditor && failed ? (
    <ErrorPage errorCode="404" isEditorPreview />
  ) : null;
};
